import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
  CircularProgress,
  Tab,
  Tabs,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import CampaignIcon from "@mui/icons-material/Campaign";
import HandshakeIcon from "@mui/icons-material/Handshake";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const CollabsRevenueDialog = ({
  open,
  onClose,
  campaigns,
  partnerships,
  onEditCampaign,
  onEditPartnership,
  formatAmount,
  formatDate,
  loading,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter active items
const activeCampaigns = campaigns; // Already filtered in parent
const activePartnerships = partnerships; // Already filtered in parent


  const totalCampaignsRevenue = activeCampaigns.reduce(
    (sum, c) => sum + parseFloat(c.campaign_sum || 0),
    0
  );

  const totalPartnershipsRevenue = activePartnerships.reduce(
    (sum, p) => sum + parseFloat(p.computed_total || 0),
    0
  );

  const totalRevenue = totalCampaignsRevenue + totalPartnershipsRevenue;

  // Search filtering
  const filteredCampaigns = activeCampaigns.filter(
    (c) =>
      searchTerm === "" ||
      c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPartnerships = activePartnerships.filter(
    (p) =>
      searchTerm === "" ||
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.creator?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Active Collaborations Revenue
              </Typography>
              <Chip
                label="Active Projects"
                color="success"
                sx={{ fontWeight: 600 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Revenue from launched campaigns and active partnerships
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 2,
                mb: 3,
              }}
            >
              <Card sx={{ bgcolor: "#f0fdf4", border: "1px solid #86efac" }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    Total Revenue
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#15803d", mt: 0.5 }}
                  >
                    {formatAmount(totalRevenue)}
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ bgcolor: "#ede9fe", border: "1px solid #c4b5fd" }}>
                <CardContent sx={{ py: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        Launched Campaigns
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, color: "#7c3aed", mt: 0.5 }}
                      >
                        {formatAmount(totalCampaignsRevenue)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {activeCampaigns.length} campaigns
                      </Typography>
                    </Box>
                    <CampaignIcon sx={{ color: "#7c3aed", fontSize: 32 }} />
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ bgcolor: "#dbeafe", border: "1px solid #93c5fd" }}>
                <CardContent sx={{ py: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        Active Partnerships
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, color: "#2563eb", mt: 0.5 }}
                      >
                        {formatAmount(totalPartnershipsRevenue)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {activePartnerships.length} partnerships
                      </Typography>
                    </Box>
                    <HandshakeIcon sx={{ color: "#2563eb", fontSize: 32 }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Tabs and Search */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(e, val) => {
                  setActiveTab(val);
                  setSearchTerm("");
                }}
              >
                <Tab
                  label={`Campaigns (${activeCampaigns.length})`}
                  icon={<CampaignIcon />}
                  iconPosition="start"
                />
                <Tab
                  label={`Partnerships (${activePartnerships.length})`}
                  icon={<HandshakeIcon />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextField
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 350 }}
              />
            </Box>

            {/* Campaigns Table */}
            {activeTab === 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f9fafb" }}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Campaign Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Revenue</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Videos</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Creators</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCampaigns.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                          <Typography color="text.secondary">
                            No active campaigns
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCampaigns.map((campaign) => (
                        <TableRow
                          key={campaign.id}
                          hover
                          sx={{ "&:hover": { bgcolor: "#f9fafb" } }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CampaignIcon
                                sx={{ color: "#7c3aed", fontSize: 20 }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {campaign.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={campaign.campaign_status}
                              size="small"
                              color="success"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: "#059669" }}
                            >
                              {formatAmount(campaign.campaign_sum)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {campaign.video_amount || 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {campaign.creators?.length || 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(campaign.proposal_date)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit Campaign">
                              <IconButton
                                size="small"
                                onClick={() => onEditCampaign(campaign)}
                                sx={{ color: "#3b82f6" }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Partnerships Table */}
            {activeTab === 1 && (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f9fafb" }}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Partnership
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Creator</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Revenue</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Deliverables
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPartnerships.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                          <Typography color="text.secondary">
                            No active partnerships
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPartnerships.map((partnership) => (
                        <TableRow
                          key={partnership.id}
                          hover
                          sx={{ "&:hover": { bgcolor: "#f9fafb" } }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <HandshakeIcon
                                sx={{ color: "#2563eb", fontSize: 20 }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {partnership.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {partnership.creator}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={partnership.status}
                              size="small"
                              color="success"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: "#059669" }}
                            >
                              {formatAmount(partnership.computed_total)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {partnership.deliverables_count || 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(partnership.proposal_date)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit Partnership">
                              <IconButton
                                size="small"
                                onClick={() => onEditPartnership(partnership)}
                                sx={{ color: "#3b82f6" }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollabsRevenueDialog;